import React from 'react'
import { connect } from 'react-redux'
import { getTranslate } from 'react-localize-redux'
import {
  Dialog,
  DialogContainer,
  Content,
  Divider,
  Footer,
  Button,
  Heading,
  Text,
  Well,
  StatusLight,
} from '@adobe/react-spectrum'
import { SPECTRUM_THEME_CLASS } from '../../themes/spectrum-theme'
import CheckmarkCircle from '@spectrum-icons/workflow/CheckmarkCircle'
import ChevronRight from '@spectrum-icons/workflow/ChevronRight'
import styles from './subscription-status-dialog.styles'
import { translateSubscriptionFeature } from './subscription-feature-translations'

interface Subscription {
  id?: string
  tier: 'basic' | 'plus'
  status: 'Active' | 'Inactive' | 'CanceledScheduled' | 'PastDue' | 'pending' | 'Pending'
  devices?: number
  startDate?: string
  currentPeriodStart?: string
  currentPeriodEnd?: string
  monthlyPrice?: number
  checkoutPending?: boolean
}

interface SubscriptionStatusDialogProps {
  open: boolean
  onClose: () => void
  subscription: Subscription
  deviceCount?: number
  stripeCustomerPortalUrl?: string
  isPending?: boolean
  readOnly?: boolean
  // Redux state
  subscriptionPlans?: any[]
  translate: (key: string, data?: Record<string, unknown>) => string
  selectedLanguage?: string
}

const SubscriptionStatusDialog: React.FC<SubscriptionStatusDialogProps> = ({
  open,
  onClose,
  subscription,
  deviceCount = 1,
  stripeCustomerPortalUrl,
  isPending = false,
  readOnly = false,
  subscriptionPlans,
  translate,
  selectedLanguage,
}) => {
  // TODO: fix — derive from subscription.tier once backend is consistent
  const isPlusSubscription = true
  const isCanceledScheduled = subscription?.status === 'CanceledScheduled'
  const isPastDue = subscription?.status === 'PastDue'

  const formatDate = (dateString?: string): string => {
    if (!dateString) return translate('general_na')
    return new Date(dateString).toLocaleDateString(selectedLanguage === 'jp-JP' ? 'ja-JP' : selectedLanguage || 'en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatCurrency = (amount?: number): string => {
    if (amount === null || amount === undefined) return translate('subscription_dialog_contact_sales')
    return `£${amount.toFixed(2)}`
  }

  return (
    <DialogContainer onDismiss={onClose}>
      {open && (
        <Dialog size="L" width={'800px'} UNSAFE_className={SPECTRUM_THEME_CLASS}>
          <>
            <Heading level={2}>{translate('subscription_dialog_my_subscription')}</Heading>
            <Divider />
            <Content>
              <div style={styles.container}>
                {/* Subscription Status Card */}
                <Well UNSAFE_style={styles.statusCard}>
                  <div style={styles.statusHeader}>
                    <div>
                      <div style={styles.statusTitleRow}>
                        <Heading level={3} margin={0} marginTop={'size-150'}>
                          {isPending ? translate('subscription_dialog_plus_pending') : translate('subscription_dialog_plus_plan')}
                        </Heading>
                        <StatusLight variant={isPending ? 'notice' : isCanceledScheduled || isPastDue ? 'negative' : 'positive'}>
                          {isPending
                            ? translate('subscription_dialog_payment_processing')
                            : isPastDue
                              ? translate('subscription_dialog_payment_overdue')
                              : isCanceledScheduled
                                ? translate('subscription_dialog_cancellation_pending')
                                : translate('active')}
                        </StatusLight>
                      </div>
                      {isPending && (
                        <Text UNSAFE_style={styles.pendingText}>
                          {translate('subscription_dialog_pending_description')}
                        </Text>
                      )}
                      {isPastDue && (
                        <Text UNSAFE_style={styles.pendingText}>
                          {translate('subscription_dialog_payment_overdue_description')}
                        </Text>
                      )}
                    </div>

                    <div style={styles.priceSection}>
                      <div style={styles.priceLine}>
                        <Text UNSAFE_className="spectrum-Heading2">
                          {formatCurrency(subscription?.monthlyPrice)}
                        </Text>
                        <Text UNSAFE_className="spectrum-Body3">
                          {translate('subscription_dialog_per_month')}
                        </Text>
                      </div>
                    </div>
                  </div>
                </Well>

                {/* Subscription Details Grid */}
                <div style={styles.detailsGrid}>
                  <Well UNSAFE_style={styles.statusCard}>
                    <div>
                      <div style={styles.detailLabel}>
                        <Text UNSAFE_className="spectrum-Body3">{translate('subscription_dialog_registered_devices')}</Text>
                      </div>
                      <Heading level={3} marginTop={'4px'} marginBottom={0}>
                        {subscription?.devices || deviceCount || 0}
                      </Heading>
                    </div>
                  </Well>

                  <Well UNSAFE_style={styles.statusCard}>
                    <div>
                      <div style={styles.detailLabel}>
                        <Text UNSAFE_className="spectrum-Body3">{translate('subscription_dialog_plan_start')}</Text>
                      </div>
                      <div style={styles.detailValue}>
                        {formatDate(subscription?.startDate)}
                      </div>
                    </div>
                  </Well>

                  <Well UNSAFE_style={styles.statusCard}>
                    <div>
                      <div style={styles.detailLabel}>
                        <Text UNSAFE_className="spectrum-Body3">{translate('current_period_start')}</Text>
                      </div>
                      <div style={styles.detailValue}>
                        {formatDate(subscription?.currentPeriodStart)}
                      </div>
                    </div>
                  </Well>

                  <Well UNSAFE_style={styles.statusCard}>
                    <div>
                      <div style={styles.detailLabel}>
                        <Text UNSAFE_className="spectrum-Body3">
                          {isCanceledScheduled ? translate('subscription_dialog_cancellation_date') : translate('subscription_dialog_next_payment_date')}
                        </Text>
                      </div>
                      <div style={styles.detailValue}>
                        {formatDate(subscription?.currentPeriodEnd)}
                      </div>
                    </div>
                  </Well>
                </div>

                {/* Benefits Section */}
                {isPlusSubscription && (() => {
                  const plusPlan = subscriptionPlans?.find((p: any) => p.id === 'plus')
                  const features = plusPlan?.features
                    ? plusPlan.features.map((feature: string) => translateSubscriptionFeature(feature, translate))
                    : [
                    translate('subscription_dialog_all_basic_features'),
                    translate('subscription_dialog_archiving'),
                    translate('subscription_dialog_geolocation_lock'),
                    translate('subscription_dialog_analytics_metrics'),
                    translate('subscription_dialog_smart_flag_filter'),
                  ]
                  return (
                    <>
                      <Divider />
                      <div>
                        <Heading level={3}>{translate('subscription_dialog_included_benefits')}</Heading>
                        <ul style={styles.benefitsGrid}>
                          {features.map((feature: string) => (
                            <li key={feature} style={styles.benefitItem}>
                              <CheckmarkCircle size="S" color="positive" />
                              <Text>{feature}</Text>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )
                })()}
              </div>
            </Content>

            <Divider />
            <Footer>
              <div style={styles.footerActions}>
                <Button variant="secondary" onPress={onClose}>
                  {translate('general_close')}
                </Button>
                {isPlusSubscription && !readOnly && (
                  <Button
                    variant="accent"
                    onPress={() => {
                      if (stripeCustomerPortalUrl) {
                        window.open(stripeCustomerPortalUrl, '_blank')
                      }
                    }}
                  >
                    {translate('subscription_dialog_manage_subscription')}
                    <ChevronRight size="S" />
                  </Button>
                )}
              </div>
            </Footer>
          </>
        </Dialog>
      )}
    </DialogContainer>
  )
}

const mapStateToProps = (state: any) => ({
  subscriptionPlans: state.appSettingReducer.subscriptionPlans,
  translate: getTranslate(state.localeReducer),
  selectedLanguage: state.languageReducer.selectedLanguage,
})

export default connect(mapStateToProps)(SubscriptionStatusDialog)
